import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  User, 
  MapPin, 
  Calendar as CalendarIcon, 
  CheckSquare, 
  MessageSquare,
  Eye,
  Edit,
  Trash2,
  Clock,
  UserCheck
} from 'lucide-react';
import { calcularTiempoTranscurrido, obtenerColorTiempo } from '@/utils/tareaUtils';

const PRIORIDADES = {
  alta: { label: 'Alta', color: 'bg-red-100 text-red-800', icon: '🔴' },
  media: { label: 'Media', color: 'bg-yellow-100 text-yellow-800', icon: '🟡' },
  baja: { label: 'Baja', color: 'bg-green-100 text-green-800', icon: '🟢' },
};

export default function TareaCard({ 
  tarea, 
  onEdit, 
  onDelete, 
  onViewDetail, 
  isExpanded, 
  onToggleExpand 
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tarea.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const prioridad = PRIORIDADES[tarea.prioridad] || PRIORIDADES.media;
  const fechaVencida = tarea.fecha_planificada && 
    new Date(tarea.fecha_planificada) < new Date() && 
    tarea.estado !== 'finalizado';

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`bg-white rounded-lg shadow-sm border-2 mb-2 cursor-move hover:shadow-md transition-all ${
        isExpanded ? 'border-emerald-500' : 'border-gray-200'
      }`}
    >
      {/* Header compacto - siempre visible */}
      <div 
        className="flex items-center justify-between p-2 cursor-pointer hover:bg-gray-50 rounded-t-lg"
        onClick={(e) => {
          e.stopPropagation();
          onToggleExpand(tarea.id);
        }}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Badge className={`text-xs ${prioridad.color} flex-shrink-0`}>
            {prioridad.icon}
          </Badge>
          <h4 className="font-semibold text-sm truncate">{tarea.titulo}</h4>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Indicadores compactos cuando está contraído */}
          {!isExpanded && (
            <>
              {tarea.subtareas && tarea.subtareas.length > 0 && (
                <Badge variant="outline" className="text-xs px-1 h-5">
                  {tarea.subtareas.filter(st => st.completada).length}/{tarea.subtareas.length}
                </Badge>
              )}
              {tarea.comentarios && tarea.comentarios.length > 0 && (
                <Badge variant="outline" className="text-xs px-1 h-5">
                  💬 {tarea.comentarios.length}
                </Badge>
              )}
            </>
          )}
          {/* Icono de expansión */}
          {isExpanded ? (
            <span className="text-emerald-600">▼</span>
          ) : (
            <span className="text-gray-400">▶</span>
          )}
        </div>
      </div>

      {/* Contenido expandible */}
      {isExpanded && (
        <div className="px-3 pb-3 pt-1 border-t border-gray-100">
          {tarea.descripcion && (
            <p className="text-xs text-gray-600 mb-2">{tarea.descripcion}</p>
          )}

          {/* Tiempo transcurrido */}
          <div className={`flex items-center text-xs mb-2 ${obtenerColorTiempo(tarea.created_at)}`}>
            <Clock className="w-3 h-3 mr-1" />
            <span className="font-semibold">⏱️ {calcularTiempoTranscurrido(tarea.created_at)}</span>
          </div>

          <div className="space-y-1 mb-2">
            {tarea.responsable_nombre && (
              <div className="flex items-center text-xs text-gray-600">
                <User className="w-3 h-3 mr-1" />
                {tarea.responsable_nombre}
              </div>
            )}

            {tarea.revisor_nombre && (
              <div className="flex items-center text-xs text-purple-600 font-semibold">
                <UserCheck className="w-3 h-3 mr-1" />
                Revisor: {tarea.revisor_nombre}
              </div>
            )}

            {tarea.lote_nombre && (
              <div className="flex items-center text-xs text-gray-600">
                <MapPin className="w-3 h-3 mr-1" />
                {tarea.lote_nombre}
              </div>
            )}

            {tarea.fecha_planificada && (
              <div className={`flex items-center text-xs ${fechaVencida ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                <CalendarIcon className="w-3 h-3 mr-1" />
                {tarea.fecha_planificada}
                {fechaVencida && ' ⚠️'}
              </div>
            )}

            {tarea.subtareas && tarea.subtareas.length > 0 && (
              <div className="flex items-center text-xs text-gray-600">
                <CheckSquare className="w-3 h-3 mr-1" />
                {tarea.subtareas.filter(st => st.completada).length}/{tarea.subtareas.length} subtareas
              </div>
            )}

            {tarea.comentarios && tarea.comentarios.length > 0 && (
              <div className="flex items-center text-xs text-gray-600">
                <MessageSquare className="w-3 h-3 mr-1" />
                {tarea.comentarios.length} comentarios
              </div>
            )}
          </div>

          {/* Botones de acción */}
          <div className="flex gap-1 mt-2 pt-2 border-t border-gray-100">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs flex-1"
              onClick={(e) => {
                e.stopPropagation();
                onViewDetail(tarea);
              }}
            >
              <Eye className="w-3 h-3 mr-1" />
              Ver
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs flex-1"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(tarea);
              }}
            >
              <Edit className="w-3 h-3 mr-1" />
              Editar
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs text-red-600"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(tarea.id);
              }}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
